import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  CreditCard,
  FileText,
  Loader2,
  Lock,
  QrCode,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";

import ModalBase from "components/ModalBase";
import ModalRealizarCotacao from "./ModalRealizarCotacao";
import {
  consultarRecarga,
  criarRecarga,
  getCarteira,
  previewTaxaCartao,
  type LancamentoCarteira,
  type MetodoRecarga,
  type PagamentoCarteira,
  type ResumoCarteira,
  avisarCarteiraAtualizada,
} from "services/carteira/carteira.service";
import { downloadCsv } from "utils/downloadCsv";

type Props = {
  show: boolean;
  title: string;
  setShowNumber: (value: React.SetStateAction<number>) => void;
  logo?: ReactNode;
};

type Etapa = "inicio" | "aguardando" | "pago";

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const dataCurta = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const PRESETS = [50, 100, 250, 500];

/** "1.234,56" / "1234.56" / "50" → número. */
const lerValor = (s: string) => {
  const limpo = s.replace(/[^\d,.]/g, "");
  const normal = limpo.includes(",") ? limpo.replace(/\./g, "").replace(",", ".") : limpo;
  const n = Number(normal);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};

const mensagemErro = (err: unknown, padrao: string) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
  (err as Error)?.message ||
  padrao;

const ROTULO_LANCAMENTO: Record<string, string> = {
  recarga: "Recarga",
  debito_envio: "Envio",
  estorno: "Estorno da recarga",
  // O cancelamento da cotacao grava `estorno_envio`. Faltava aqui, e o
  // fallback `?? l.tipo` mostrava a string crua no extrato do cliente.
  estorno_envio: "Estorno do envio",
  ajuste: "Ajuste",
};

const ModalCarteira = ({ show, title, setShowNumber, logo }: Props) => {
  const [resumo, setResumo] = useState<ResumoCarteira | null>(null);
  const [carregando, setCarregando] = useState(false);

  const [valorTexto, setValorTexto] = useState("100");
  const [metodo, setMetodo] = useState<MetodoRecarga>("PIX");
  const [taxa, setTaxa] = useState<{ taxa: number; total: number } | null>(null);

  const [etapa, setEtapa] = useState<Etapa>("inicio");
  const [pagamento, setPagamento] = useState<PagamentoCarteira | null>(null);
  const [criando, setCriando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [novaCotacao, setNovaCotacao] = useState(false);

  const valor = lerValor(valorTexto);
  const habilitada = resumo?.habilitada === true;
  const limites = habilitada ? resumo.limites : { minimo: 10, maximo: 20000 };
  const valorValido = valor >= limites.minimo && valor <= limites.maximo;

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setResumo(await getCarteira());
    } catch {
      setResumo(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  // Ao abrir: recarrega o saldo e volta para o início.
  useEffect(() => {
    if (!show) return;
    setEtapa("inicio");
    setPagamento(null);
    carregar();
  }, [show, carregar]);

  // Prévia do total no cartão, com a taxa calculada no servidor.
  useEffect(() => {
    if (metodo !== "CREDIT_CARD" || !valorValido) {
      setTaxa(null);
      return;
    }
    const t = setTimeout(() => {
      previewTaxaCartao(valor)
        .then((r) => setTaxa({ taxa: r.taxa, total: r.total }))
        .catch(() => setTaxa(null));
    }, 300);
    return () => clearTimeout(t);
  }, [metodo, valor, valorValido]);

  // Enquanto espera o pagamento, consulta a cada 4s. O servidor confere no
  // Asaas, então funciona mesmo se o webhook atrasar.
  useEffect(() => {
    if (!show || etapa !== "aguardando" || !pagamento) return;
    let ativo = true;

    const tick = async () => {
      try {
        const r = await consultarRecarga(pagamento.id);
        if (!ativo) return;

        if (r.pagamento.status === "PAGO") {
          setPagamento((p) => (p ? { ...p, ...r.pagamento } : r.pagamento));
          setEtapa("pago");
          carregar();
          avisarCarteiraAtualizada();
        } else if (["FALHOU", "CANCELADO", "ESTORNADO"].includes(r.pagamento.status)) {
          toast.error("O pagamento não foi concluído", {
            description: "Gere uma nova recarga para tentar de novo.",
          });
          setEtapa("inicio");
          carregar();
        }
      } catch {
        /* tenta de novo no próximo ciclo */
      }
    };

    const id = setInterval(tick, 4000);
    return () => {
      ativo = false;
      clearInterval(id);
    };
  }, [show, etapa, pagamento, carregar]);

  const iniciarRecarga = async () => {
    if (!valorValido) return;
    setCriando(true);
    try {
      const r = await criarRecarga(valor, metodo);
      setPagamento(r.pagamento);
      setEtapa("aguardando");
      if (metodo === "CREDIT_CARD" && r.pagamento.invoice_url) {
        window.open(r.pagamento.invoice_url, "_blank", "noopener");
      }
    } catch (err) {
      toast.error("Não foi possível gerar a recarga", {
        description: mensagemErro(err, "Tente novamente em instantes."),
      });
    } finally {
      setCriando(false);
    }
  };

  const copiarPix = async (codigo: string | null) => {
    if (!codigo) return;
    try {
      await navigator.clipboard.writeText(codigo);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast.error("Não foi possível copiar. Selecione o código e copie manualmente.");
    }
  };

  const lancamentos = useMemo(
    () => (habilitada ? resumo.lancamentos : []),
    [habilitada, resumo],
  );
  const pendentes = useMemo(
    () => (habilitada ? resumo.pendentes : []),
    [habilitada, resumo],
  );

  const saldo = habilitada ? resumo.saldo : 0;
  const emProcessamento = pendentes.reduce((acc, p) => acc + (p.total || 0), 0);

  /** Débitos de envio do mês corrente. */
  const gastoNoMes = useMemo(() => {
    const agora = new Date();
    return lancamentos
      .filter((l) => {
        if (l.tipo !== "debito_envio") return false;
        const d = new Date(l.criado_em);
        return d.getMonth() === agora.getMonth() && d.getFullYear() === agora.getFullYear();
      })
      .reduce((acc, l) => acc + Math.abs(l.valor), 0);
  }, [lancamentos]);

  /**
   * Quantos envios cada valor paga, pela média do que a conta já gastou.
   * Sem histórico não há o que estimar — some, em vez de chutar um número.
   */
  const mediaEnvio = useMemo(() => {
    const envios = lancamentos.filter((l) => l.tipo === "debito_envio");
    if (!envios.length) return null;
    const soma = envios.reduce((acc, l) => acc + Math.abs(l.valor), 0);
    return soma / envios.length;
  }, [lancamentos]);

  const exportarExtrato = () => {
    if (!lancamentos.length) return;
    const linhas = [
      "data;tipo;descricao;valor_brl",
      ...lancamentos.map((l) =>
        [
          new Date(l.criado_em).toLocaleString("pt-BR"),
          ROTULO_LANCAMENTO[l.tipo] ?? l.tipo,
          (l.descricao ?? "").replace(/;/g, ","),
          l.valor.toFixed(2).replace(".", ","),
        ].join(";"),
      ),
    ].join("\r\n");

    downloadCsv(new Blob([linhas], { type: "text/csv;charset=utf-8" }), "extrato-carteira.csv");
  };

  const totalAPagar =
    metodo === "CREDIT_CARD" && taxa ? taxa.total : valor;

  return (
    <>
      <ModalBase
        show={show}
        onClose={() => setShowNumber(-1)}
        titulo={title}
        icone={logo ?? <Wallet />}
        subtitulo="Saldo pré-pago para pagar fretes e etiquetas na hora"
        largura="1000px"
        corpoSemPadding
        acoes={
          <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
            <Lock className="h-3 w-3" />
            Ambiente seguro
          </span>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-[1fr_300px]">
          {/* COLUNA PRINCIPAL */}
          <div className="space-y-5 px-6 py-5">
            {/* Saldo */}
            <div className="rounded-2xl bg-brand-sidebar px-5 py-4 text-white">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] text-white/60">Saldo disponível</p>
                  <p className="mt-0.5 text-3xl font-bold leading-none">
                    {carregando && !resumo ? "…" : brl(saldo)}
                  </p>
                  <p className="mt-2 text-[11px] text-white/50">
                    {etapa === "pago"
                      ? "Atualizado agora · recarga confirmada"
                      : saldo <= 0
                        ? "Atualizado agora · sem saldo, emissão de etiquetas pausada"
                        : "Atualizado agora"}
                  </p>
                </div>

                <div className="flex gap-6">
                  <div>
                    <p className="text-[10px] text-white/50">Em processamento</p>
                    <p className="text-sm font-semibold">{brl(emProcessamento)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50">Gasto este mês</p>
                    <p className="text-sm font-semibold">{brl(gastoNoMes)}</p>
                  </div>
                </div>
              </div>
            </div>

            {!habilitada && !carregando ? (
              <div className="rounded-xl border border-dashed border-slate-200 px-5 py-10 text-center">
                <p className="text-sm font-medium text-slate-500">
                  A carteira ainda não está liberada para esta conta.
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Enquanto isso, a remessa continua sendo paga por boleto.
                </p>
              </div>
            ) : etapa === "inicio" ? (
              <>
                <section>
                  <h3 className="mb-2 text-sm font-semibold text-slate-800">
                    Quanto você quer adicionar?
                  </h3>
                  <div className="grid grid-cols-4 gap-2">
                    {PRESETS.map((p) => {
                      const ativo = valor === p;
                      const envios = mediaEnvio ? Math.floor(p / mediaEnvio) : null;
                      return (
                        <button
                          key={p}
                          onClick={() => setValorTexto(String(p))}
                          className={`rounded-xl border-2 px-2 py-2.5 text-center transition-colors ${
                            ativo
                              ? "border-brand-sidebar bg-slate-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <span className="block text-sm font-semibold text-slate-900">
                            {brl(p).replace(/\s/g, " ")}
                          </span>
                          {envios ? (
                            <span className="block text-[10px] text-slate-400">
                              ≈ {envios} {envios === 1 ? "envio" : "envios"}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2">
                    <label className="flex flex-1 items-center gap-2">
                      <span className="text-xs text-slate-500">Outro valor</span>
                      <span className="text-xs text-slate-400">R$</span>
                      <input
                        value={valorTexto}
                        onChange={(e) => setValorTexto(e.target.value)}
                        inputMode="decimal"
                        className="w-28 bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-300"
                        placeholder="0,00"
                      />
                    </label>
                    <span className="text-[11px] text-slate-400">
                      mínimo {brl(limites.minimo)}
                    </span>
                  </div>
                  {!valorValido && valorTexto.trim() !== "" && (
                    <p className="mt-1 text-[11px] text-amber-600">
                      Informe um valor entre {brl(limites.minimo)} e {brl(limites.maximo)}.
                    </p>
                  )}
                </section>

                <section>
                  <h3 className="mb-2 text-sm font-semibold text-slate-800">
                    Como você quer pagar?
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <OpcaoPagamento
                      ativo={metodo === "PIX"}
                      onClick={() => setMetodo("PIX")}
                      icone={<QrCode className="h-4 w-4" />}
                      titulo="PIX"
                      selo={{ texto: "Sem taxa", tom: "verde" }}
                      apoio="Confirma em segundos"
                    />
                    <OpcaoPagamento
                      ativo={metodo === "CREDIT_CARD"}
                      onClick={() => setMetodo("CREDIT_CARD")}
                      icone={<CreditCard className="h-4 w-4" />}
                      titulo="Cartão de crédito"
                      selo={
                        habilitada
                          ? {
                              texto: `+ ${resumo.taxa_cartao_pct_efetiva.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}%`,
                              tom: "cinza",
                            }
                          : undefined
                      }
                      apoio="Visa, Mastercard, Elo"
                    />
                  </div>
                </section>

                <dl className="space-y-1.5 rounded-xl border border-slate-200 px-4 py-3 text-xs">
                  <Linha termo="Valor" valor={brl(valor)} />
                  <Linha
                    termo={metodo === "PIX" ? "Taxa do PIX" : "Taxa do cartão"}
                    valor={
                      metodo === "PIX"
                        ? "Grátis"
                        : taxa
                          ? brl(taxa.taxa)
                          : "—"
                    }
                    destaque={metodo === "PIX" ? "text-emerald-600" : undefined}
                  />
                  <div className="flex items-center justify-between border-t border-slate-100 pt-1.5">
                    <dt className="font-semibold text-slate-800">Você paga</dt>
                    <dd className="font-semibold text-slate-900">{brl(totalAPagar)}</dd>
                  </div>
                  <Linha
                    termo="Entra na sua carteira"
                    valor={brl(valor)}
                    destaque="text-emerald-600"
                  />
                </dl>

                <button
                  onClick={iniciarRecarga}
                  disabled={!valorValido || criando}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-sidebar text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {criando ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Lock className="h-4 w-4" />
                  )}
                  {metodo === "PIX"
                    ? `Gerar PIX de ${brl(valor)}`
                    : `Pagar ${brl(totalAPagar)} no cartão`}
                </button>

                {pendentes.length > 0 && (
                  <p className="text-[11px] text-slate-400">
                    {pendentes.length === 1
                      ? "Há 1 recarga aguardando pagamento."
                      : `Há ${pendentes.length} recargas aguardando pagamento.`}{" "}
                    Elas entram no saldo assim que o banco confirmar.
                  </p>
                )}
              </>
            ) : etapa === "aguardando" && pagamento ? (
              <AguardandoPagamento
                pagamento={pagamento}
                copiado={copiado}
                onCopiar={() => copiarPix(pagamento.pix_copia_cola)}
                onAlterarValor={() => setEtapa("inicio")}
              />
            ) : etapa === "pago" && pagamento ? (
              <Confirmado
                valor={pagamento.valor}
                invoiceUrl={pagamento.invoice_url}
                onCotar={() => setNovaCotacao(true)}
              />
            ) : null}
          </div>

          {/* EXTRATO */}
          <aside className="flex flex-col justify-between gap-4 border-slate-100 bg-slate-50/50 px-5 py-5 md:border-l">
            <div className="space-y-4">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">Extrato</h3>
                  <button
                    onClick={exportarExtrato}
                    disabled={!lancamentos.length}
                    className="text-xs font-medium text-brand-primary transition-opacity hover:underline disabled:opacity-40 disabled:hover:no-underline"
                  >
                    Exportar
                  </button>
                </div>

                {!lancamentos.length ? (
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-5 text-center">
                    <p className="text-[11px] leading-relaxed text-slate-400">
                      Nenhuma movimentação ainda. Recargas e cada etiqueta paga aparecem
                      aqui, com comprovante.
                    </p>
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {lancamentos.slice(0, 8).map((l) => (
                      <ItemExtrato key={l.id} lancamento={l} />
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Como funciona
                </h4>
                <ol className="space-y-2.5">
                  {[
                    <>
                      <strong className="font-semibold text-slate-700">
                        Você adiciona saldo
                      </strong>{" "}
                      por PIX ou cartão, em reais.
                    </>,
                    <>
                      <strong className="font-semibold text-slate-700">
                        Cada etiqueta é debitada
                      </strong>{" "}
                      na hora da emissão, com o valor que você viu na cotação.
                    </>,
                    <>
                      <strong className="font-semibold text-slate-700">
                        Etiqueta cancelada volta
                      </strong>{" "}
                      para a carteira, pelo valor pago.
                    </>,
                  ].map((texto, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-brand-sidebar text-[9px] font-bold text-white">
                        {i + 1}
                      </span>
                      <p className="text-[11px] leading-snug text-slate-500">{texto}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className="space-y-1.5 border-t border-slate-200 pt-3">
              <Rodape icone={<Lock className="h-3 w-3" />} texto="Pagamento processado pela Asaas" />
              <Rodape
                icone={<ShieldCheck className="h-3 w-3" />}
                texto="Não guardamos dados do seu cartão"
              />
              <Rodape
                icone={<FileText className="h-3 w-3" />}
                texto="Comprovante de cada recarga"
              />
            </div>
          </aside>
        </div>
      </ModalBase>

      <ModalRealizarCotacao
        show={novaCotacao}
        setShow={setNovaCotacao}
        title="Realizar cotação"
        logo={<Wallet />}
      />
    </>
  );
};

export default ModalCarteira;

/* ------------------------------------------------------------------ */

const AguardandoPagamento = ({
  pagamento,
  copiado,
  onCopiar,
  onAlterarValor,
}: {
  pagamento: PagamentoCarteira;
  copiado: boolean;
  onCopiar: () => void;
  onAlterarValor: () => void;
}) => {
  const restante = useExpiracao(pagamento.pix_expira_em);

  if (pagamento.metodo === "CREDIT_CARD") {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 px-5 py-10 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
        <p className="text-sm font-medium text-slate-700">Aguardando o pagamento</p>
        <p className="max-w-[40ch] text-xs leading-relaxed text-slate-400">
          Conclua o pagamento na aba que abrimos. O saldo entra assim que a operadora
          confirmar — pode fechar esta janela.
        </p>
        {pagamento.invoice_url && (
          <a
            href={pagamento.invoice_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-brand-primary hover:underline"
          >
            Abrir a página de pagamento de novo
          </a>
        )}
        <button
          onClick={onAlterarValor}
          className="mt-1 text-xs text-slate-500 hover:text-slate-700"
        >
          Alterar valor
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:flex-row">
      <div className="flex flex-col items-center gap-2">
        <div className="grid h-[180px] w-[180px] place-items-center rounded-xl border border-slate-200 bg-white p-2">
          {pagamento.pix_imagem ? (
            <img
              src={
                pagamento.pix_imagem.startsWith("data:")
                  ? pagamento.pix_imagem
                  : `data:image/png;base64,${pagamento.pix_imagem}`
              }
              alt="QR Code do PIX"
              className="h-full w-full object-contain"
            />
          ) : (
            <QrCode className="h-10 w-10 text-slate-300" />
          )}
        </div>
        {restante && (
          <span className="rounded-full bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700">
            Expira em {restante}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 text-xs font-medium text-brand-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-primary" />
          Aguardando pagamento
        </p>
        <p className="mt-1 text-2xl font-bold text-slate-900">{brl(pagamento.valor)}</p>
        <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
          O saldo entra assim que o banco confirmar, em geral em segundos. Pode fechar esta
          janela.
        </p>

        <p className="mt-3 text-[11px] font-medium text-slate-500">PIX copia e cola</p>
        <div className="mt-1 flex items-center gap-2">
          <input
            readOnly
            value={pagamento.pix_copia_cola ?? ""}
            onFocus={(e) => e.currentTarget.select()}
            className="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 font-mono text-[11px] text-slate-600 outline-none"
          />
          <button
            onClick={onCopiar}
            className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-brand-sidebar px-4 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          >
            {copiado ? <Check className="h-3.5 w-3.5" /> : null}
            {copiado ? "Copiado" : "Copiar"}
          </button>
        </div>

        <button
          onClick={onAlterarValor}
          className="mt-4 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
        >
          Alterar valor
        </button>
      </div>
    </div>
  );
};

const Confirmado = ({
  valor,
  invoiceUrl,
  onCotar,
}: {
  valor: number;
  invoiceUrl: string | null;
  onCotar: () => void;
}) => (
  <div className="flex flex-col items-center px-4 py-8 text-center">
    <div className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white">
      <Check className="h-7 w-7" strokeWidth={3} />
    </div>
    <h3 className="text-lg font-semibold text-slate-900">Pagamento confirmado</h3>
    <p className="mt-1 max-w-[44ch] text-xs leading-relaxed text-slate-400">
      {brl(valor)} já estão na sua carteira.
    </p>

    <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
      {invoiceUrl && (
        <a
          href={invoiceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          Baixar comprovante
        </a>
      )}
      <button
        onClick={onCotar}
        className="rounded-lg bg-brand-sidebar px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
      >
        Fazer uma cotação
      </button>
    </div>
  </div>
);

/** Contagem regressiva do PIX, no formato mm:ss. */
const useExpiracao = (iso: string | null) => {
  const [restante, setRestante] = useState<string | null>(null);

  useEffect(() => {
    if (!iso) {
      setRestante(null);
      return;
    }

    const calcular = () => {
      const faltam = new Date(iso).getTime() - Date.now();
      if (!Number.isFinite(faltam) || faltam <= 0) {
        setRestante(null);
        return;
      }
      const total = Math.floor(faltam / 1000);
      const min = Math.floor(total / 60);
      setRestante(`${min}:${String(total % 60).padStart(2, "0")}`);
    };

    calcular();
    const id = setInterval(calcular, 1000);
    return () => clearInterval(id);
  }, [iso]);

  return restante;
};

const ItemExtrato = ({ lancamento }: { lancamento: LancamentoCarteira }) => {
  const entrada = lancamento.valor >= 0;
  return (
    <li className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <span
        className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full ${
          entrada ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
        }`}
      >
        {entrada ? (
          <ArrowUpRight className="h-3.5 w-3.5" />
        ) : (
          <ArrowDownLeft className="h-3.5 w-3.5" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-slate-800">
          {ROTULO_LANCAMENTO[lancamento.tipo] ?? lancamento.tipo}
        </span>
        <span className="block truncate text-[10px] text-slate-400">
          {dataCurta(lancamento.criado_em)}
          {lancamento.descricao ? ` · ${lancamento.descricao}` : ""}
        </span>
      </span>
      <span
        className={`shrink-0 text-xs font-semibold ${
          entrada ? "text-emerald-600" : "text-slate-600"
        }`}
      >
        {entrada ? "+" : "−"} {brl(Math.abs(lancamento.valor))}
      </span>
    </li>
  );
};

const Linha = ({
  termo,
  valor,
  destaque,
}: {
  termo: string;
  valor: string;
  destaque?: string;
}) => (
  <div className="flex items-center justify-between gap-2">
    <dt className="text-slate-500">{termo}</dt>
    <dd className={destaque ?? "font-medium text-slate-800"}>{valor}</dd>
  </div>
);

const OpcaoPagamento = ({
  ativo,
  onClick,
  icone,
  titulo,
  selo,
  apoio,
}: {
  ativo: boolean;
  onClick: () => void;
  icone: ReactNode;
  titulo: string;
  selo?: { texto: string; tom: "verde" | "cinza" };
  apoio: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={ativo}
    className={`flex items-start gap-2.5 rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
      ativo ? "border-brand-sidebar bg-slate-50" : "border-slate-200 hover:border-slate-300"
    }`}
  >
    <span
      className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border-2 ${
        ativo ? "border-brand-sidebar" : "border-slate-300"
      }`}
    >
      {ativo && <span className="h-2 w-2 rounded-full bg-brand-sidebar" />}
    </span>
    <span className="min-w-0">
      <span className="flex items-center gap-1.5">
        <span className="text-slate-500">{icone}</span>
        <span className="text-sm font-semibold text-slate-900">{titulo}</span>
        {selo && (
          <span
            className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
              selo.tom === "verde"
                ? "bg-emerald-50 text-emerald-700"
                : "bg-slate-100 text-slate-500"
            }`}
          >
            {selo.texto}
          </span>
        )}
      </span>
      <span className="mt-0.5 block text-[11px] text-slate-400">{apoio}</span>
    </span>
  </button>
);

const Rodape = ({ icone, texto }: { icone: ReactNode; texto: string }) => (
  <p className="flex items-center gap-2 text-[10px] text-slate-400">
    <span className="shrink-0">{icone}</span>
    {texto}
  </p>
);
